// NOTICE: KAPTION-INTEGRATION
// whatsapp-message-merging — the hold/merge policy, free of Vue and Vuex.
//
// An agent's consecutive plain WhatsApp texts wait on the client (in the same
// pending state Undo uses) and go out as ONE ordinary message. This module owns
// the timing policy only; the store wiring lives in
// composables/useKaptionMessageMerge.js.
//
// The hold ends at max(undo floor, min(idle deadline, cap)):
//   - idle deadline = last activity (a held fragment or a keystroke with text in
//     the composer) + the level's idle time;
//   - cap = first held fragment + the level's cap (bounds the merge extension);
//   - undo floor = last fragment + the agent's Undo seconds (0 when Undo is off),
//     so merging only ever extends Undo, never shortens it.
//
// When the customer replies mid-hold the group is PAUSED instead of sent: no
// timer, nothing goes out until the agent confirms with Enter on an empty
// composer (or takes the text back). While paused, a closing 24 h window only
// raises a warning; it never sends unconfirmed text.
import { isKaptionAggregationEligible } from 'dashboard/helper/kaptionDeliveryMode';

export const KAPTION_MERGE_LEVELS = Object.freeze({
  OFF: 'off', // "Inmediato"
  BALANCED: 'balanced', // "Equilibrado"
  GROUP: 'group', // "Agrupar"
});

export const KAPTION_MERGE_LEVEL_CONFIG = Object.freeze({
  [KAPTION_MERGE_LEVELS.BALANCED]: { idleMs: 4000, capMs: 12000 },
  [KAPTION_MERGE_LEVELS.GROUP]: { idleMs: 8000, capMs: 25000 },
});

// Cris 2026-09-27: with the flag on, merging is ON by default, conservatively.
// An account with no explicit admin default gets Equilibrado (4 s idle, 12 s
// cap); an explicit admin default (including Inmediato) or an agent's own
// choice still wins.
export const KAPTION_MERGE_DEFAULT_LEVEL = KAPTION_MERGE_LEVELS.BALANCED;

export const KAPTION_MERGE_FLUSH_REASONS = Object.freeze({
  IDLE: 'idle',
  CAP: 'cap',
  MANUAL: 'manual', // Enter / send with an empty composer
  INCOMING: 'incoming', // the customer wrote
  OTHER_SENDER: 'other_sender', // another agent, the assistant, an automation
  NON_MERGEABLE: 'non_mergeable', // attachment, template, quote, translation
  WINDOW: 'window', // the 24 h window is about to close
  LEAVE: 'leave', // conversation switch, page hide
  STATUS: 'status', // resolve, snooze, reassign
  SETTING: 'setting', // flag or level turned off mid-hold
  OVERFLOW: 'overflow', // the next fragment would pass 4,096 chars / 20 fragments
});

export const KAPTION_MERGE_JOINER = '\n';
export const WHATSAPP_TEXT_LIMIT = 4096;
export const KAPTION_MERGE_MAX_FRAGMENTS = 20;
export const KAPTION_MERGE_WINDOW_SAFETY_MS = 5000;
export const KAPTION_MERGE_TYPING_REFRESH_MS = 20000;

const VALID_LEVELS = Object.values(KAPTION_MERGE_LEVELS);
const validLevel = value => (VALID_LEVELS.includes(value) ? value : null);

/**
 * The level that applies to this agent: their own choice, else the account
 * default, else Inmediato. A locked account always uses its default.
 */
export const resolveKaptionMergeLevel = ({
  uiSettings = {},
  accountSettings = {},
} = {}) => {
  const accountDefault =
    validLevel(accountSettings?.kaption_merge_default) ||
    KAPTION_MERGE_DEFAULT_LEVEL;
  if (accountSettings?.kaption_merge_locked) return accountDefault;
  return validLevel(uiSettings?.kaption_merge_level) || accountDefault;
};

// A live reply has one translation of the agent's original text. Keep that
// original alongside the merged customer-facing text for the translation UI.
const liveTranslation = payload => {
  const translations = payload?.contentAttributes?.kaption_translations;
  if (!translations || Object.keys(translations).length !== 1) return null;
  const [language, entry] = Object.entries(translations)[0];
  return language &&
    entry &&
    typeof entry.text === 'string' &&
    entry.text.trim() &&
    entry.source_language &&
    entry.target_language
    ? { language, entry }
    : null;
};

/** Public plain text, including a valid live translation of the agent's text. */
export const isKaptionMergeableFragment = payload =>
  isKaptionAggregationEligible(payload) ||
  (isKaptionAggregationEligible({
    ...payload,
    contentAttributes: {
      ...(payload?.contentAttributes || {}),
      kaption_translations: undefined,
    },
  }) &&
    !!liveTranslation(payload));

const sameTranslationMode = (first, next) => {
  const a = liveTranslation(first);
  const b = liveTranslation(next);
  if (!a || !b) return !a && !b;
  return (
    a.language === b.language &&
    a.entry.source_language === b.entry.source_language &&
    a.entry.target_language === b.entry.target_language &&
    a.entry.provider === b.entry.provider &&
    a.entry.context_aware === b.entry.context_aware
  );
};

export const fragmentContent = payload =>
  payload?.message ?? payload?.content ?? '';

const defaultId = () =>
  window.crypto?.randomUUID?.() ||
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

export class KaptionMessageMergeQueue {
  /**
   * @param {Object} deps
   * @param {Function} deps.send (payload, group) => Promise — sends one merged message
   * @param {Function} [deps.onChange] (conversationId, snapshot|null, meta) => void
   * @param {Function} [deps.onTyping] (conversationId) => void — customer-facing typing
   * @param {Function} [deps.windowExpiresAt] (conversationId) => ms epoch | null
   */
  constructor({
    send,
    onChange = () => {},
    onTyping = () => {},
    windowExpiresAt = () => null,
    now = () => Date.now(),
    setTimer = (fn, ms) => setTimeout(fn, ms),
    clearTimer = id => clearTimeout(id),
    createId = defaultId,
  }) {
    this.send = send;
    this.onChange = onChange;
    this.onTyping = onTyping;
    this.windowExpiresAt = windowExpiresAt;
    this.now = now;
    this.setTimer = setTimer;
    this.clearTimer = clearTimer;
    this.createId = createId;
    this.groups = new Map();
    this.chains = new Map();
  }

  hasGroup(conversationId) {
    return this.groups.has(conversationId);
  }

  conversationIds() {
    return [...this.groups.keys()];
  }

  static joined(group) {
    return group.fragments.map(f => f.content).join(KAPTION_MERGE_JOINER);
  }

  // Paused (the customer replied): no deadline at all — only the agent sends.
  windowWarning(group) {
    const expiresAt = this.windowExpiresAt(group.conversationId);
    return (
      !!expiresAt && expiresAt - KAPTION_MERGE_WINDOW_SAFETY_MS <= this.now()
    );
  }

  deadline(group) {
    const config = KAPTION_MERGE_LEVEL_CONFIG[group.level] || {
      idleMs: 0,
      capMs: 0,
    };
    const capAt = group.firstHeldAt + config.capMs;
    if (group.paused) return { at: null, capAt, reason: null };
    const idleAt = Math.min(group.lastActivityAt + config.idleMs, capAt);
    const undoAt = group.lastFragmentAt + group.undoMs;
    const at = Math.max(idleAt, undoAt);
    let reason = KAPTION_MERGE_FLUSH_REASONS.IDLE;
    if (undoAt <= idleAt && idleAt >= capAt) {
      reason = KAPTION_MERGE_FLUSH_REASONS.CAP;
    }
    const expiresAt = this.windowExpiresAt(group.conversationId);
    if (expiresAt && expiresAt < at + KAPTION_MERGE_WINDOW_SAFETY_MS) {
      return {
        at: this.now(),
        capAt,
        reason: KAPTION_MERGE_FLUSH_REASONS.WINDOW,
      };
    }
    return { at, capAt, reason };
  }

  snapshot(conversationId) {
    const group = this.groups.get(conversationId);
    if (!group) return null;
    const { at, capAt } = this.deadline(group);
    return {
      id: group.id,
      conversationId,
      level: group.level,
      count: group.fragments.length,
      content: KaptionMessageMergeQueue.joined(group),
      firstHeldAt: group.firstHeldAt,
      lastActivityAt: group.lastActivityAt,
      deadlineAt: at,
      capAt,
      paused: !!group.paused,
      pausedReason: group.pausedReason || null,
      windowWarning: !!group.paused && this.windowWarning(group),
      sender: group.fragments[0]?.payload?.sender,
    };
  }

  emit(conversationId, meta = {}) {
    this.onChange(conversationId, this.snapshot(conversationId), meta);
  }

  reschedule(group) {
    if (group.timerId) this.clearTimer(group.timerId);
    group.timerId = null;
    if (group.paused) {
      // Never send on our own; only wake up to warn when the window is closing.
      const expiresAt = this.windowExpiresAt(group.conversationId);
      if (expiresAt && !this.windowWarning(group)) {
        group.timerId = this.setTimer(
          () => {
            group.timerId = null;
            if (this.groups.get(group.conversationId) === group) {
              this.emit(group.conversationId);
            }
          },
          Math.max(0, expiresAt - KAPTION_MERGE_WINDOW_SAFETY_MS - this.now())
        );
      }
      return;
    }
    const { at, reason } = this.deadline(group);
    if (reason === KAPTION_MERGE_FLUSH_REASONS.WINDOW) {
      this.flush(group.conversationId, reason);
      return;
    }
    const currentTime = this.now();
    if (
      group.level === KAPTION_MERGE_LEVELS.GROUP &&
      currentTime - group.typingAt >= KAPTION_MERGE_TYPING_REFRESH_MS
    ) {
      group.typingAt = currentTime;
      this.onTyping(group.conversationId);
    }
    group.timerId = this.setTimer(
      () => {
        group.timerId = null;
        this.flush(group.conversationId, this.deadline(group).reason);
      },
      Math.max(0, at - currentTime)
    );
  }

  /**
   * Holds one plain text. Returns the group snapshot.
   * @param {Object} payload ReplyBox message payload (conversationId, message, …)
   * @param {{level: string, undoMs?: number}} options
   */
  enqueue(payload, { level, undoMs = 0 }) {
    const { conversationId } = payload;
    const content = fragmentContent(payload);
    let group = this.groups.get(conversationId);
    if (group && !sameTranslationMode(group.fragments[0].payload, payload)) {
      this.flush(conversationId, KAPTION_MERGE_FLUSH_REASONS.NON_MERGEABLE);
      group = null;
    }
    if (
      group &&
      (KaptionMessageMergeQueue.joined(group).length +
        KAPTION_MERGE_JOINER.length +
        content.length >
        WHATSAPP_TEXT_LIMIT ||
        group.fragments.length >= KAPTION_MERGE_MAX_FRAGMENTS)
    ) {
      this.flush(conversationId, KAPTION_MERGE_FLUSH_REASONS.OVERFLOW);
      group = null;
    }
    const currentTime = this.now();
    if (!group) {
      group = {
        id: this.createId(),
        conversationId,
        level,
        undoMs,
        fragments: [],
        firstHeldAt: currentTime,
        lastActivityAt: currentTime,
        lastFragmentAt: currentTime,
        typingAt: currentTime,
        timerId: null,
      };
      this.groups.set(conversationId, group);
      this.onTyping(conversationId);
    }
    group.fragments.push({ content, payload, addedAt: currentTime });
    group.level = level;
    group.undoMs = undoMs;
    group.lastActivityAt = currentTime;
    group.lastFragmentAt = currentTime;
    this.reschedule(group);
    // reschedule may have flushed (window closing); only emit a live group.
    if (this.groups.get(conversationId) === group) this.emit(conversationId);
    return this.snapshot(conversationId);
  }

  /** A keystroke in the composer. Only text in the box extends the hold. */
  noteTyping(conversationId, hasText) {
    const group = this.groups.get(conversationId);
    if (!group || !hasText) return;
    group.lastActivityAt = this.now();
    this.reschedule(group);
    if (this.groups.get(conversationId) === group) this.emit(conversationId);
  }

  static buildMergedPayload(group, reason) {
    const base = group.fragments[0].payload;
    const baseAttributes = { ...(base.contentAttributes || {}) };
    delete baseAttributes.kaption_merge;
    const translation = liveTranslation(base);
    if (translation) {
      baseAttributes.kaption_translations = {
        [translation.language]: {
          ...translation.entry,
          text: group.fragments
            .map(fragment => liveTranslation(fragment.payload).entry.text)
            .join(KAPTION_MERGE_JOINER),
        },
      };
    }
    return {
      ...base,
      message: KaptionMessageMergeQueue.joined(group),
      contentAttributes: {
        ...baseAttributes,
        kaption_merge: {
          id: group.id,
          source_count: group.fragments.length,
          first_held_at: new Date(group.firstHeldAt).toISOString(),
          level: group.level,
          flush_reason: reason,
        },
      },
    };
  }

  chain(conversationId, fn) {
    const previous = this.chains.get(conversationId) || Promise.resolve();
    const next = previous.catch(() => {}).then(fn);
    this.chains.set(conversationId, next);
    return next;
  }

  /** Removes the group and returns its merged payload without sending it. */
  take(conversationId, reason) {
    const group = this.groups.get(conversationId);
    if (!group) return null;
    if (group.timerId) this.clearTimer(group.timerId);
    this.groups.delete(conversationId);
    return {
      group,
      payload: KaptionMessageMergeQueue.buildMergedPayload(group, reason),
    };
  }

  /** Sends the held group now as one message. Resolves when it is created. */
  flush(conversationId, reason) {
    const taken = this.take(conversationId, reason);
    if (!taken) {
      return this.chains.get(conversationId) || Promise.resolve();
    }
    this.emit(conversationId, {
      removed: 'flush',
      groupId: taken.group.id,
      reason,
    });
    return this.chain(conversationId, () =>
      this.send(taken.payload, taken.group)
    );
  }

  /**
   * The customer replied mid-hold (Cris, 2026-09-26): do NOT send. Keep the
   * held texts merged as one pending message, stop the timer, and wait for the
   * agent to confirm with Enter on an empty composer (or take it back).
   */
  pause(conversationId, reason = KAPTION_MERGE_FLUSH_REASONS.INCOMING) {
    const group = this.groups.get(conversationId);
    if (!group) return null;
    group.paused = true;
    group.pausedReason = reason;
    this.reschedule(group);
    this.emit(conversationId);
    return this.snapshot(conversationId);
  }

  /** Deshacer while paused: the whole merged text goes back to the composer. */
  takeBack(conversationId) {
    const group = this.groups.get(conversationId);
    if (!group) return null;
    if (group.timerId) this.clearTimer(group.timerId);
    this.groups.delete(conversationId);
    this.emit(conversationId, { removed: 'cancel', groupId: group.id });
    return { content: KaptionMessageMergeQueue.joined(group), group };
  }

  /** Flushes the held text first, then runs fn (e.g. an attachment send). */
  runAfterFlush(conversationId, reason, fn) {
    this.flush(conversationId, reason);
    return this.chain(conversationId, fn);
  }

  flushAll(reason) {
    return Promise.all(
      this.conversationIds().map(id => this.flush(id, reason))
    );
  }

  /**
   * Deshacer / Esc: removes the LAST fragment and returns it so the caller can
   * put it back in the composer. The only fragment → the hold is cancelled.
   */
  undoLast(conversationId) {
    const group = this.groups.get(conversationId);
    if (!group) return null;
    const fragment = group.fragments.pop();
    if (!group.fragments.length) {
      if (group.timerId) this.clearTimer(group.timerId);
      this.groups.delete(conversationId);
      this.emit(conversationId, { removed: 'cancel', groupId: group.id });
      return fragment;
    }
    group.lastFragmentAt = group.fragments[group.fragments.length - 1].addedAt;
    this.reschedule(group);
    this.emit(conversationId);
    return fragment;
  }
}
